/**
 * CvLibraryPage.js — RC 1.41 P0.1.C-r9 + RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE
 *                  + RC 1.49.x SOURCE_CV_REPLACE_OPTION_A
 *
 * Source CV surface for /documents → "Source CV" tab.
 *
 * RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE (operator-locked, 2026-05-06):
 * Source CVs are PRIVATE REFERENCE MATERIAL. They are never exported
 * and never previewable — globally. The "View" button + modal preview
 * have been retired. Tailored CV variant previews remain unaffected
 * (they live on the Variants tab and the wizard).
 *
 * RC 1.49.x SOURCE_CV_REPLACE_OPTION_A (operator-locked, 2026-05-07):
 * Users can upload a NEW Source CV at any time. The "Upload new
 * Source CV" CTA opens a confirmation modal that explains
 * Option-A semantics:
 *   • Future analyses + future generation use the new CV.
 *   • Existing application packs and generated documents stay
 *     unchanged (still tied to the CV they were built with).
 *   • The replacement CV is also private reference material —
 *     not previewed, not exported.
 * The upload calls the existing `POST /api/base-cvs/upload` with
 * `make_primary=true`. The backend demotes the previous primary
 * and inserts a new row; old rows are NEVER deleted, so packs
 * and variants that reference them keep resolving.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, AlertCircle, Loader2, CheckCircle2, ShieldCheck, RefreshCw, Upload,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { cachedFetch, peek, invalidate, KEYS } from '@/lib/listCache';
import { toast } from 'sonner';

const BUILD_MARKER = 'RC1.49.x-SOURCE_CV_REPLACE_OPTION_A-2026-05-07';

// RC 1.59 Slice 2 — Documents tab cache adoption (Tier 1). The
// Source CV row only changes on Replace upload (mutated below),
// so a 5-minute TTL with stale-while-revalidate gives a flash-free
// revisit while still picking up out-of-band edits within the
// session. Invalidated explicitly after a successful upload.
const SOURCE_CV_TTL_MS = 5 * 60_000;

export default function CvLibraryPage() {
  const { user } = useAuth();

  const [primaryCv, setPrimaryCv] = useState(() => {
    const seed = peek(KEYS.sourceCvActive(user?.id), SOURCE_CV_TTL_MS);
    return seed.hit ? (seed.data ?? null) : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!user?.id) return false;
    const seed = peek(KEYS.sourceCvActive(user?.id), SOURCE_CV_TTL_MS);
    return !seed.hit;
  });
  const [fetchError, setFetchError] = useState('');

  // RC 1.49.x SOURCE_CV_REPLACE_OPTION_A — confirmation modal +
  // upload state. The flow is strictly Option A: a fresh Source CV
  // is uploaded as a new `base_cvs` row with `make_primary=true`,
  // the previous primary is demoted server-side, and old packs /
  // variants / job_scores keep their original references intact.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const fetchPrimary = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      // RC 1.59 Slice 2 — wrap the supabase query in cachedFetch so
      // re-entering /documents → Source CV tab within the TTL window
      // serves the cached row without re-hitting the database.
      const row = await cachedFetch(
        KEYS.sourceCvActive(user.id),
        async () => {
          const { data, error } = await supabase
            .from('base_cvs')
            .select('id, file_url, original_filename, is_primary, parsed_text, parsed_json, created_at, updated_at')
            .eq('user_id', user.id)
            .eq('is_primary', true)
            .maybeSingle();
          if (error) throw new Error(error.message);
          return data || null;
        },
        { ttlMs: SOURCE_CV_TTL_MS },
      );
      setPrimaryCv(row || null);
    } catch (err) {
      setFetchError(err?.message || 'Failed to load Source CV.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) fetchPrimary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // RC 1.49.x SOURCE_CV_REPLACE_OPTION_A — open the native file
  // picker. Operator-reported bug (2026-05-07): Safari / Firefox /
  // iOS Safari treat `setTimeout(0)` callbacks as outside the
  // user-gesture window, so a chain of
  //     setConfirmOpen(false) → setTimeout(0) → input.click()
  // would silently fail to open the OS picker even though the
  // confirm button was clicked. The fix is to call `.click()`
  // SYNCHRONOUSLY inside the user-gesture event, BEFORE the
  // React state update that closes the dialog. The OS picker is
  // already in front of the dialog, which can fade out behind it
  // without affecting the gesture chain.
  const onConfirmedPickFile = useCallback(() => {
    setUploadError('');
    fileInputRef.current?.click();
    setConfirmOpen(false);
  }, []);

  const onFileChosen = useCallback(async (event) => {
    const file = event.target?.files?.[0];
    // Reset input value so picking the same file again still fires
    // a change event next time.
    if (event.target) event.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('original_filename', file.name);
      fd.append('make_primary', 'true');
      // apiFetch already attaches the Bearer token. Do NOT set the
      // Content-Type header — the browser sets the multipart
      // boundary for us.
      const res = await apiFetch('/api/base-cvs/upload', {
        method: 'POST',
        body: fd,
      });
      if (!res || !res.cv_id) {
        throw new Error('Upload succeeded but the response was empty.');
      }
      toast.success('Source CV updated. Future analyses will use the new file.');
      // RC 1.59 Slice 2 — bust cache so the refresh below lands on a
      // fresh server response (the new primary row), not the stale
      // pre-replace cached row.
      invalidate(KEYS.sourceCvActive(user?.id));
      // Refresh the source-card so filename + parsed-char count + date
      // flip immediately. Existing packs / variants / job_scores are
      // untouched per Option A semantics.
      await fetchPrimary();
    } catch (err) {
      const detail = err?.detail || err?.message || 'Upload failed. Please try again.';
      setUploadError(typeof detail === 'string' ? detail : 'Upload failed. Please try again.');
      toast.error('Could not update Source CV.');
    } finally {
      setUploading(false);
    }
  }, [fetchPrimary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cv-library-page">
      {fetchError && (
        <div
          className="flex items-start gap-2 rounded-lg bg-ndor-error-light border border-ndor-error-border p-3"
          data-testid="cv-library-fetch-error"
        >
          <AlertCircle className="h-4 w-4 text-ndor-error mt-0.5 shrink-0" />
          <p className="text-sm text-ndor-ink-secondary">{fetchError}</p>
        </div>
      )}

      {primaryCv ? (
        <>
          {/* RC 1.54 (2026-05-12 PM, operator-locked) — Source CV
              card unified: file + parsed status + the Upload action
              live on the same row, so the action feels naturally
              attached to the document it operates on. The previous
              separate "replace zone" + multi-paragraph trust note
              were collapsed into a single calm line below the card.
              No backend / logic changes — the confirm modal +
              `handleUpload` are untouched.

              Forensic build marker is preserved as a non-prominent
              `data-build` attribute on the card root so QA can still
              read it from devtools without it cluttering the UI. */}
          <div
            className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="cv-library-source-card"
            data-build={BUILD_MARKER}
          >
            <div className="w-11 h-11 rounded-lg bg-ndor-success-light flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-5 w-5 text-ndor-success" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ndor-ink truncate" data-testid="cv-library-source-filename">
                {primaryCv.original_filename || 'Source CV'}
              </p>
              <p className="text-xs text-ndor-ink-secondary" data-testid="cv-library-source-status">
                {primaryCv?.parsed_json?.text_length
                  ? `Parsed · ${Number(primaryCv.parsed_json.text_length).toLocaleString()} chars`
                  : 'Saved'}
                {primaryCv.created_at && ` · Uploaded ${new Date(primaryCv.created_at).toLocaleDateString()}`}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setUploadError(''); setConfirmOpen(true); }}
              disabled={uploading}
              data-testid="cv-library-replace-upload-button"
              className="shrink-0 self-stretch sm:self-auto"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1.5" />
              )}
              {uploading ? 'Uploading…' : 'Replace Source CV'}
            </Button>
          </div>

          {uploadError && (
            <div
              className="flex items-start gap-2 rounded-md bg-ndor-error-light border border-ndor-error-border p-3"
              data-testid="cv-library-upload-error"
            >
              <AlertCircle className="h-4 w-4 text-ndor-error mt-0.5 shrink-0" />
              <p className="text-sm text-ndor-ink-secondary">{uploadError}</p>
            </div>
          )}

          {/* Single short explanatory line — combines the previous
              "you can upload at any time" paragraph + the P0.1.A
              trust note into one calm sentence. */}
          <p
            className="flex items-start gap-2 text-[11px] text-ndor-ink-secondary leading-relaxed"
            data-testid="cv-library-source-trust-note"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-ndor-success mt-0.5 shrink-0" />
            <span>
              Stored as parsed text only — never previewed or exported.
              Replacing it doesn&rsquo;t affect packs already created.
            </span>
          </p>
        </>
      ) : (
        // Defensive: if a candidate ever lands here without a Source
        // CV (legacy account, manual DB intervention, partial
        // onboarding) we explain what happened and route them to
        // support rather than offering a broken-in-this-tab uploader.
        <div
          className="rounded-xl border-2 border-dashed border-ndor-warning/50 bg-ndor-warning-light/30 p-5 text-center"
          data-testid="cv-library-missing-state"
        >
          <div className="mx-auto w-11 h-11 rounded-full bg-ndor-warning-light flex items-center justify-center mb-3">
            <AlertCircle className="h-5 w-5 text-ndor-warning" />
          </div>
          <p className="text-sm font-medium text-ndor-ink">
            No Source CV on file
          </p>
          <p className="text-xs text-ndor-ink-secondary mt-1.5 max-w-md mx-auto leading-relaxed">
            Your Source CV is set during onboarding. If you don&rsquo;t see one here, please contact support so we can restore your account state.
          </p>

          {/* RC 1.49.x Slice 8 — calm reviewer-guidance hint mirroring
              Slice 7. Sequencing only — not a walkthrough or product
              tour. Renders only when no Source CV is on file, so
              existing-user UX is untouched. */}
          <div
            className="mt-5 mx-auto max-w-md rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-left"
            data-testid="documents-source-empty-reviewer-hint"
          >
            <p className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
              Recommended next step
            </p>
            <ol className="mt-2 space-y-1.5 text-xs text-ndor-ink-secondary leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink">1.</span>
                <span>
                  <span className="font-medium text-ndor-ink">Upload your first Source CV</span>
                  {' '}— this is the starting point before any job analysis. It&rsquo;s normally captured during onboarding.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink">2.</span>
                <span>Once your Source CV is on file, head to <Link to="/jobs" className="text-ndor-info hover:underline">Browse jobs</Link> to import a role.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink">3.</span>
                <span>Run <span className="font-medium text-ndor-ink">Analyse Fit</span> to see how your CV matches the role.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink">4.</span>
                <span>Generate a tailored CV, cover letter, and apply-ready application pack.</span>
              </li>
            </ol>
          </div>
        </div>
      )}

      <p className="text-xs text-ndor-ink-muted flex items-start gap-1.5 leading-relaxed">
        <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          You have one Source CV. CV variants and cover letters for specific jobs are generated from it inside the job application flow.
        </span>
      </p>

      {/* RC 1.49.x SOURCE_CV_REPLACE_OPTION_A — hidden file input.
          Driven by the modal-confirmed "Upload new Source CV" CTA. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={onFileChosen}
        data-testid="cv-library-replace-file-input"
      />

      {/* Confirmation modal — explains Option A semantics in plain
          English before any picker opens. The user must explicitly
          accept before we trigger the file picker. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="max-w-md"
          data-testid="cv-library-replace-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-tight">
              Upload a new Source CV?
            </DialogTitle>
            <DialogDescription className="text-sm text-ndor-ink-secondary">
              Just so you know what changes and what doesn&rsquo;t.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 py-2 text-sm text-ndor-ink-secondary leading-relaxed">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-ndor-success mt-0.5 shrink-0" />
              <span>
                <strong className="text-ndor-ink">Future analyses and new generated documents</strong>
                {' '}(Analyse Fit runs, tailored CV variants, cover letters, application packs)
                will use the new Source CV.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
              <span>
                <strong className="text-ndor-ink">Existing application packs and generated documents stay unchanged</strong>
                {' '}— they remain tied to the Source CV used when they were created.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
              <span>
                Uploaded Source CVs remain
                {' '}<strong className="text-ndor-ink">private reference material</strong>
                {' '}— they are not previewed or exported anywhere in NDORFlow.
              </span>
            </li>
          </ul>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              data-testid="cv-library-replace-cancel-button"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirmedPickFile}
              data-testid="cv-library-replace-confirm-button"
              className="w-full sm:w-auto"
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Choose file…
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
